import React, { useState, useEffect, useRef } from 'react';
import { RecordVideos } from 'components/Video/RecordVideos';
import { TournamentLink } from 'views/Tournament/TournamentLink';
import { GameScoreTabs } from 'views/PvP/game/GameScoreTabs';
import { makeStyles } from '@material-ui/core/styles';
import _ from 'lodash';
import {CaptureTheFlagGameQuery, CaptureTheFlagGameData, CaptureTheFlagGameVars} from '@pvplevelup/core/dist/queries/game/capturetheflag';
import { useQuery } from '@apollo/client';
import { Typography, Select, MenuItem, useMediaQuery } from '@material-ui/core';
import { ZoneInformation, IZone, IMap } from '@pvplevelup/core/dist/interfaces/maps/IMaps';
import { IPlayerLocation } from '@pvplevelup/core/dist/interfaces/Game/IGame';
import MapRenderer, { MapDataTypes, PointData } from 'components/Map/MapRenderer';
import { MapIcons, MapThumbnailIcons } from 'utilities/map_icons';
import ReactResizeDetector from 'react-resize-detector';
import { GameHeader } from '../PvP/game/GameHeader';
import { WinnerBanner } from '../PvP/game/WinnerBanner';
import { GameScore, getTeamScores } from '../PvP/game/GameScore';
import { DeathBreakdown } from '../PvP/game/DeathBreakdown';
import { PlayerScore } from '../PvP/game/PvPGame';
import { RouteComponentProps } from 'react-router-dom';
import { TeamColors } from 'utilities/team_colors';
import { breakPoint } from 'components/Character/CharacterStats';
import { blackColor } from 'assets/jss/material-dashboard-react';
import { hhmmss } from '@pvplevelup/core/dist/utility/time';



const useStyles = makeStyles(theme => ({
  root:{
    flexGrow:1
  },
  gameMode: {
    color: 'grey'
  },
  hr: {
    border: '1px solid #424242',
    borderBottom: 0
  },
  mainContent: {
    flex: '1 1 auto',
    overflowY: 'auto'
  },
  footer: {
    height: '40px'
  },
  main: {
    height:'100%'
  },
  noGrow: {
    flex: '0 0 auto'
  },
  background: {
    position: "absolute",
    zIndex: -1,
    height: "100%",
    width: "100%",
    display: "block",
    top: "0",
    left: "0",
    backgroundSize: "cover",
    backgroundPosition: "center center",
    "&:after": {
      position: "absolute",
      zIndex: "3",
      width: "100%",
      height: "100%",
      content: '""',
      display: "block",
      background: blackColor,
      opacity: ".65"
    }
  }
}));

interface Props{
}

interface RouteParams {
  id?: string;
}


export const CaptureTheFlagSummary: React.FC<Props & RouteComponentProps<RouteParams>> = (props) => {
  const classes = useStyles();

  let [currentLocale, setCurrentLocale] = useState<string | undefined>("");
  let [imageWidth, setImageWidth] = useState(5);
  let [containterWidth, setContainerWidth] = useState(0);
  const imageWidthRef = useRef(imageWidth);
  imageWidthRef.current = imageWidth;
  const [imageResizeTimeout, setImageResizeTimeout] = useState<NodeJS.Timeout | undefined>();
  let [isResizing, setIsResizing] = useState(false);

  const [zone, setZone] = useState<IZone | null>(null);
  const [map, setMap] = useState<IMap | null>(null);
  let [ position, setPosition ] = useState<IPlayerLocation | null>(null);

  let [teamScores, setTeamScores] = useState<Array<PlayerScore[]>>([]);

  const { subscribeToMore, data, error, refetch } = useQuery<CaptureTheFlagGameData,CaptureTheFlagGameVars>(
    CaptureTheFlagGameQuery,
    { variables: {id:props.match.params.id},fetchPolicy:'network-only' }
  );

  useEffect(()=>{
    let z = ZoneInformation[data?.capturetheflag_game?.zone || ""] || null;
    let m = (z) ? z.mapInfo[data?.capturetheflag_game?.map || ""] : null;
    setZone(z);
    setMap(m);
  },[data?.capturetheflag_game?._id])

  const updateScores = () => {
    if(data?.capturetheflag_game){
      let newTeamScores:Array<PlayerScore[]> = getTeamScores(data?.capturetheflag_game?.players || []);
      setTeamScores(newTeamScores);
    }
  }

  useEffect(()=>{
    updateScores();
  },[data?.capturetheflag_game,data?.capturetheflag_game?.deaths?.length]);

  const matches = useMediaQuery(`(max-width:${breakPoint}px)`);

  return (
    <React.Fragment>
      {data?.capturetheflag_game &&
        <section className={"game-container"} style={{
          gridTemplateAreas: matches ? `
                                        'header header'
                                        'content content'
                                        'sidebar sidebar'
                                      ` 
                                      : `
                                          'header header'
                                          'content sidebar'
                                        `,
        }}>
          <div className="game-information">
            <GameHeader map={map?.name} zone={zone?.name} mode={data.capturetheflag_game?.mode}
              endCondition={data.capturetheflag_game?.endCondition} time={data.capturetheflag_game?.time}
              scoreToWin={data.capturetheflag_game?.scoreToWin} gameTimer={hhmmss(data.capturetheflag_game?.timeElapsed || 0)} teamMMR={data.capturetheflag_game?.teamMMR} teamCMMR={data.capturetheflag_game?.teamCMMR}
              actions={(data.capturetheflag_game as any)?.tournament ? <TournamentLink tournament={(data.capturetheflag_game as any)?.tournament} /> : undefined}
            />
            {data.capturetheflag_game?.winner != null &&
              <WinnerBanner team={data.capturetheflag_game?.winner} />
            }
          </div>
          <section className="game-content">
            <div className="game-score">
              <GameScoreTabs game={data.capturetheflag_game}><GameScore teamScores={teamScores} game={data.capturetheflag_game} /></GameScoreTabs>
            </div>
            <RecordVideos 
              entries={(data.capturetheflag_game.players || []).map((p: any) => ({user: p.user, name: p.name || p.character?.name, video: p.video}))} />
          </section>
          <div className="game-sidebar">
            <div className="summary-information">
              <DeathBreakdown deaths={data.capturetheflag_game?.deaths} players={data.capturetheflag_game?.players} style={{maxHeight:'75vh'}} />
            </div>
          </div>
          <ReactResizeDetector handleWidth handleHeight targetDomEl={window.document.body} onResize={(width,height) => {
            if(containterWidth != width)
              setIsResizing(true);
            setContainerWidth(width);
          }} />
          <div
            className={classes.background}
            style={{ backgroundImage: "url(" + MapThumbnailIcons[data.capturetheflag_game.zone || ""][data.capturetheflag_game.map || ""] + ")" }}
          />
        </section>
      }
    </React.Fragment>
    
  )
}